import { Pencil, Plus, Trash2 } from 'lucide-react';
import * as React from 'react';

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Combobox,
  Input,
  toast,
} from '@/components/cargofl-ui';
import { PageContainer } from '@/components/shell/page-container';
import { PILL_CLASS } from '@/lib/badges';
import {
  useCreateDepartment,
  useCreateDesignation,
  useDeleteDepartment,
  useDeleteDesignation,
  useUpdateDepartment,
  useUpdateDesignation,
} from '@/lib/org-api';
import { flattenDepartments, useDepartments, useDesignations } from '@/lib/people-api';

const ROOT = '__root__';

function errorMessage(err: unknown): string | undefined {
  return err instanceof Error ? err.message : undefined;
}

/**
 * The id of a department's parent, read back out of its own label.
 *
 * `flattenDepartments` gives "Technology > Cloud & DevOps" and no parentId, and the
 * path is the parent - everything before the last separator names it. Derived here
 * rather than widening that shared type, which every other caller uses only for a
 * picker label. Returns ROOT for a top-level department, and for the impossible case
 * where the parent path names nothing, which is the same thing to a dropdown.
 */
function parentIdOf(
  label: string,
  all: Array<{ id: string; label: string }>,
): string {
  const parts = label.split(' > ');
  if (parts.length < 2) return ROOT;
  const parentLabel = parts.slice(0, -1).join(' > ');
  return all.find((d) => d.label === parentLabel)?.id ?? ROOT;
}

/**
 * Departments and designations.
 *
 * Deliberately flat lists with an indent, not a drag-and-drop tree: re-parenting is
 * rare enough that a parent dropdown covers it, and a drag surface is a lot of code
 * for something HR does twice a year.
 */
export function OrganizationPage() {
  const { data: tree } = useDepartments();
  const { data: designations } = useDesignations();
  const departments = React.useMemo(() => flattenDepartments(tree ?? []), [tree]);

  const [deptName, setDeptName] = React.useState('');
  const [deptParent, setDeptParent] = React.useState(ROOT);
  const [title, setTitle] = React.useState('');

  // One row at a time, in both lists: an id, plus the draft it is holding. Editing two
  // rows at once is not a thing anyone wants and is a lot more state to keep honest.
  const [editingDept, setEditingDept] = React.useState<string | null>(null);
  const [draftDeptName, setDraftDeptName] = React.useState('');
  const [draftDeptParent, setDraftDeptParent] = React.useState(ROOT);
  const [editingTitle, setEditingTitle] = React.useState<string | null>(null);
  const [draftTitle, setDraftTitle] = React.useState('');

  const createDept = useCreateDepartment();
  const updateDept = useUpdateDepartment();
  const deleteDept = useDeleteDepartment();
  const createDesignation = useCreateDesignation();
  const updateDesignation = useUpdateDesignation();
  const deleteDesignation = useDeleteDesignation();

  /**
   * Both saves send only what changed and close the row on success.
   *
   * The row stays open on failure, holding what was typed. A duplicate name is the
   * likely refusal and the API names the clash — dropping back to the old value would
   * make the reader retype it to find out what it said.
   */
  async function saveDepartment(id: string) {
    const name = draftDeptName.trim();
    if (!name) return;
    try {
      await updateDept.mutateAsync({
        id,
        name,
        parentId: draftDeptParent === ROOT ? null : draftDeptParent,
      });
      setEditingDept(null);
    } catch (err) {
      toast.error('Could not rename that department', { description: errorMessage(err) });
    }
  }

  async function saveDesignation(id: string) {
    const title = draftTitle.trim();
    if (!title) return;
    try {
      await updateDesignation.mutateAsync({ id, title });
      setEditingTitle(null);
    } catch (err) {
      toast.error('Could not rename that designation', { description: errorMessage(err) });
    }
  }

  async function addDepartment(e: React.FormEvent) {
    e.preventDefault();
    if (!deptName.trim()) return;
    try {
      await createDept.mutateAsync({
        name: deptName.trim(),
        parentId: deptParent === ROOT ? null : deptParent,
      });
      setDeptName('');
      setDeptParent(ROOT);
    } catch (err) {
      toast.error('Could not add department', { description: errorMessage(err) });
    }
  }

  async function addDesignation(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    try {
      await createDesignation.mutateAsync({ title: title.trim() });
      setTitle('');
    } catch (err) {
      toast.error('Could not add designation', { description: errorMessage(err) });
    }
  }

  return (
    <PageContainer>
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Departments</CardTitle>
            <CardDescription>
              Sub-departments nest under a parent. A department with members cannot be deleted.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <form onSubmit={(e) => void addDepartment(e)} className="flex flex-wrap gap-2">
              <Input
                value={deptName}
                onChange={(e) => setDeptName(e.target.value)}
                placeholder="Department name"
                className="min-w-[160px] flex-1"
              />
              <Combobox
                className="w-[180px]"
                aria-label="Parent department"
                value={deptParent}
                // Select-only: the box beside it is already the create field, and a
                // second one would be two ways to add a department on one form.
                onChange={setDeptParent}
                options={[
                  { value: ROOT, label: 'Top level' },
                  ...departments.map((d) => ({
                    value: d.id,
                    label: d.label,
                    hint: `${d.employeeCount}`,
                  })),
                ]}
                searchPlaceholder="Search departments"
                emptyText="No department matches"
              />
              <Button hint={"Add a department"} type="submit" disabled={createDept.isPending} className="gap-1.5">
                <Plus className="size-4" />
                Add
              </Button>
            </form>

            <ul className="divide-y rounded-md border">
              {departments.length === 0 && (
                <li className="px-3 py-6 text-center text-sm text-muted-foreground">
                  No departments yet.
                </li>
              )}
              {departments.map((d) => {
                const depth = d.label.split(' > ').length - 1;
                const leaf = d.label.split(' > ').at(-1) ?? d.label;

                /**
                 * Editing in the row, not in a dialog.
                 *
                 * Renaming is the whole interaction, and a modal for one text field is a
                 * screen between you and a typo. The parent picker sits beside it because
                 * moving a department under another is the only other thing anyone does
                 * here, and it is the same save.
                 */
                if (editingDept === d.id) {
                  return (
                    <li key={d.id} className="flex flex-wrap items-center gap-2 px-3 py-2">
                      <Input
                        autoFocus
                        value={draftDeptName}
                        onChange={(e) => setDraftDeptName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') void saveDepartment(d.id);
                          if (e.key === 'Escape') setEditingDept(null);
                        }}
                        aria-label={`Rename ${leaf}`}
                        className="h-8 min-w-[140px] flex-1 text-sm"
                      />
                      <Combobox
                        className="w-[180px]"
                        aria-label={`Parent of ${leaf}`}
                        value={draftDeptParent}
                        onChange={setDraftDeptParent}
                        options={[
                          { value: ROOT, label: 'Top level' },
                          // Itself excluded: a department cannot be its own parent, and
                          // offering it only to have the API refuse is a worse answer
                          // than not offering it. Deeper cycles are caught server-side.
                          ...departments
                            .filter((o) => o.id !== d.id)
                            .map((o) => ({ value: o.id, label: o.label })),
                        ]}
                        searchPlaceholder="Search departments"
                        emptyText="No department matches"
                      />
                      <Button hint={"Save the department"}
                        size="sm"
                        disabled={draftDeptName.trim() === '' || updateDept.isPending}
                        onClick={() => void saveDepartment(d.id)}
                      >
                        {updateDept.isPending ? 'Saving…' : 'Save'}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingDept(null)}>
                        Cancel
                      </Button>
                    </li>
                  );
                }

                return (
                  <li key={d.id} className="flex items-center gap-2 px-3 py-2">
                    <span
                      className="min-w-0 flex-1 truncate text-sm"
                      style={{ paddingLeft: `${depth * 16}px` }}
                    >
                      {depth > 0 && <span className="text-muted-foreground">↳ </span>}
                      {leaf}
                    </span>
                    <Badge variant="soft-muted" className={PILL_CLASS}>
                      {d.employeeCount} {d.employeeCount === 1 ? 'person' : 'people'}
                    </Badge>
                    {/* No headcount guard on rename, unlike delete: renaming keeps every
                        member where they are, so a department with 24 people is exactly
                        the one most likely to need its name fixed. */}
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Rename ${leaf}`}
                      onClick={() => {
                        setEditingDept(d.id);
                        setDraftDeptName(leaf);
                        setDraftDeptParent(parentIdOf(d.label, departments));
                      }}
                    >
                      <Pencil className="size-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Delete ${leaf}`}
                      // The API refuses anyway; disabling here saves a round trip
                      // and explains the rule before the click.
                      disabled={d.employeeCount > 0 || deleteDept.isPending}
                      onClick={() => {
                        void deleteDept
                          .mutateAsync(d.id)
                          .catch((err: unknown) =>
                            toast.error('Could not delete', { description: errorMessage(err) }),
                          );
                      }}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Designations</CardTitle>
            <CardDescription>
              Job titles. These carry no permissions — that is the role on each employee.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <form onSubmit={(e) => void addDesignation(e)} className="flex gap-2">
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Senior Engineer"
                className="flex-1"
              />
              <Button hint={"Add a designation"} type="submit" disabled={createDesignation.isPending} className="gap-1.5">
                <Plus className="size-4" />
                Add
              </Button>
            </form>

            <ul className="divide-y rounded-md border">
              {(designations ?? []).length === 0 && (
                <li className="px-3 py-6 text-center text-sm text-muted-foreground">
                  No designations yet.
                </li>
              )}
              {(designations ?? []).map((d) =>
                editingTitle === d.id ? (
                  <li key={d.id} className="flex items-center gap-2 px-3 py-2">
                    <Input
                      autoFocus
                      value={draftTitle}
                      onChange={(e) => setDraftTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') void saveDesignation(d.id);
                        if (e.key === 'Escape') setEditingTitle(null);
                      }}
                      aria-label={`Rename ${d.title}`}
                      className="h-8 flex-1 text-sm"
                    />
                    <Button hint={"Save the designation"}
                      size="sm"
                      disabled={draftTitle.trim() === '' || updateDesignation.isPending}
                      onClick={() => void saveDesignation(d.id)}
                    >
                      {updateDesignation.isPending ? 'Saving…' : 'Save'}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditingTitle(null)}>
                      Cancel
                    </Button>
                  </li>
                ) : (
                  <li key={d.id} className="flex items-center gap-2 px-3 py-2">
                    <span className="min-w-0 flex-1 truncate text-sm">{d.title}</span>
                    <Badge variant="soft-muted" className={PILL_CLASS}>
                      {d.employeeCount}
                    </Badge>
                    {/* Renaming is offered even when people hold the title, where
                        deleting is not: a rename keeps every holder and keeps a KRA
                        group pinned to it, so nothing downstream moves. */}
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Rename ${d.title}`}
                      onClick={() => {
                        setEditingTitle(d.id);
                        setDraftTitle(d.title);
                      }}
                    >
                      <Pencil className="size-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Delete ${d.title}`}
                      disabled={d.employeeCount > 0 || deleteDesignation.isPending}
                      onClick={() => {
                        void deleteDesignation
                          .mutateAsync(d.id)
                          .catch((err: unknown) =>
                            toast.error('Could not delete', { description: errorMessage(err) }),
                          );
                      }}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </li>
                ),
              )}
            </ul>
          </CardContent>
        </Card>
      </div>
    </PageContainer>
  );
}
